/**
 * Wiki 全局埋点（对齐旧 wiki packages/shared burialPoint / reportWikiEvent）
 * POST {origin}/v1/wiki/ck/report，header game-alias = 当前游戏 alias
 */

import { readWikiUser } from './wiki-auth';

const GUEST_UID_KEY = 'guest_uid';
const DIALOG_REPORT_UNIKEY_SESSION_KEY = 'wiki_dialog_report_unikey';
const PROD_REPORT_ORIGIN = 'https://www.gamekee.com';
const PRE_REPORT_ORIGIN = 'http://www.pre.gamekee.com';
const REPORT_PATH = '/v1/wiki/ck/report';
/** click 后保留 trace_id，由 modal / page 等后续事件消费 */
const TRACE_CONSUME_EVENTS = new Set(['modal', 'page']);

const PORTAL_SEGMENTS = new Set([
  'wikis',
  'updates',
  'characters',
  'profile',
  'v2',
  'user',
  'heroes',
  'layout-report',
  'figma-layout',
]);

export type BurialPointOptions = Record<string, unknown>;

export type BurialReportPayload = BurialPointOptions & {
  event_name: string;
  sdk_ua_plat: 'pc' | 'wap';
  params: string;
  uid?: number | string | null;
  uuid?: string;
  game_id?: number | string | null;
  page_url?: string;
  query?: string;
  trace_id?: string;
};

function guid() {
  return 'xxxxxxxx-xxxx-uxxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

export function createUniqueKey(length = 8) {
  return (
    Math.random().toString().substring(2).substring(0, length) +
    Date.now().toString(36)
  );
}

/** 生成当前点击 DOM 的 epath（对齐旧 wiki getEPath） */
export function getEPath(el: Element | null) {
  const path: string[] = [];
  let node: Element | null = el;
  let foundRoot = false;
  while (node && !foundRoot) {
    let selector = node.nodeName.toLowerCase();
    if (node.id) {
      selector += `#${node.id}`;
      if (node.id === 'app' || node.id === '__next' || node.id === 'root') foundRoot = true;
    } else if (node.className && typeof node.className === 'string') {
      const firstClass = node.className.trim().split(/\s+/)[0];
      if (firstClass) selector += `.${firstClass}`;
    }
    path.unshift(selector);
    if (node === document.body || node === document.documentElement) {
      foundRoot = true;
    }
    node = node.parentElement;
  }
  return path.join(' > ');
}

function setDialogReportUnikey() {
  try {
    sessionStorage.setItem(
      DIALOG_REPORT_UNIKEY_SESSION_KEY,
      createUniqueKey(14),
    );
  } catch {
    /* ignore */
  }
}

function getTraceId() {
  try {
    return sessionStorage.getItem(DIALOG_REPORT_UNIKEY_SESSION_KEY) || '';
  } catch {
    return '';
  }
}

function consumeTraceId() {
  try {
    sessionStorage.removeItem(DIALOG_REPORT_UNIKEY_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/** 线上 www.gamekee.com → 正式域；本地及其他 → pre */
export function getBurialReportOrigin() {
  if (typeof window === 'undefined') return PRE_REPORT_ORIGIN;
  const { protocol, hostname } = window.location;
  if (protocol === 'https:' && hostname === 'www.gamekee.com') return PROD_REPORT_ORIGIN;
  return PRE_REPORT_ORIGIN;
}

/** 从路径取当前游戏 alias；门户等无游戏路径时用 www */
export function getGameAliasFromPath(pathname?: string) {
  const path =
    pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '');
  const segment = path.split('/').filter(Boolean)[0] || '';
  if (!segment || PORTAL_SEGMENTS.has(segment)) return 'www';
  return segment;
}

export function ensureGuestUid() {
  if (typeof window === 'undefined') return '';
  try {
    let guestUid = localStorage.getItem(GUEST_UID_KEY);
    if (!guestUid) {
      guestUid = guid();
      localStorage.setItem(GUEST_UID_KEY, guestUid);
    }
    return guestUid;
  } catch {
    return '';
  }
}

function getGuestUid() {
  if (typeof window === 'undefined') return '';
  try {
    return localStorage.getItem(GUEST_UID_KEY) || '';
  } catch {
    return '';
  }
}

/** 根据 UA 判断 pc / wap */
export function getSdkUaPlat(): 'pc' | 'wap' {
  if (typeof navigator === 'undefined') return 'pc';
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  return isMobile ? 'wap' : 'pc';
}

export function toBurialReportData(
  eventName: string,
  options: BurialPointOptions = {},
): BurialReportPayload {
  const sdk_ua_plat = getSdkUaPlat();
  return {
    ...options,
    sdk_ua_plat,
    params: JSON.stringify({
      sdk_ua_plat,
      ...options,
    }),
    event_name: eventName,
  };
}

function getCommonReportContext() {
  if (typeof window === 'undefined') return null;
  const user = readWikiUser();
  const guestUid = getGuestUid() || ensureGuestUid();
  return {
    uid: user?.uid ?? null,
    uuid: guestUid,
    game_id: null as number | string | null,
    page_url: window.location.pathname,
    query: window.location.search,
    trace_id: getTraceId(),
  };
}

/**
 * wiki 行为上报统一入口
 */
export function reportWikiEvent(data: BurialReportPayload) {
  const ctx = getCommonReportContext();
  if (!ctx) return Promise.resolve();
  if (TRACE_CONSUME_EVENTS.has(data.event_name)) consumeTraceId();

  const body = {
    ...ctx,
    ...data,
    page_url: data.page_url || ctx.page_url,
  };
  const origin = getBurialReportOrigin();
  const gameAlias = getGameAliasFromPath();

  return fetch(`${origin}${REPORT_PATH}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'game-alias': gameAlias,
    },
    body: JSON.stringify(body),
    keepalive: true,
  })
    .then((response) => {
      if (!response.ok) throw new Error(`burial report ${response.status}`);
      return response;
    })
    .catch(() => undefined);
}

/**
 * 埋点方法（对齐 vueMixin.burialPoint）
 * @param pointName 埋点名称
 * @param options 埋点参数
 */
export function burialPoint(
  pointName: string,
  options: BurialPointOptions = {},
) {
  if (typeof window === 'undefined') return;
  void reportWikiEvent(toBurialReportData(pointName, options));
}

export function getPageViewBurialPayload(): BurialPointOptions {
  const devicePlatform = getSdkUaPlat() === 'pc' ? 1 : 2;
  return {
    value: readWikiUser()?.uid ? 1 : 0,
    devicePlatform,
  };
}

/**
 * 全局点击埋点（对齐旧 wiki linkInterceptor 主链路部分）
 * document capture 阶段上报 click + epath / data-report-key
 */
export function bindGlobalClickBurial() {
  if (typeof document === 'undefined') return () => undefined;

  const onClick = (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const href = window.location.href;
    if (
      href.includes('/editor') ||
      href.includes('/tj-editor') ||
      href.includes('/custom-wiki/detail')
    ) {
      return;
    }

    setDialogReportUnikey();

    const reportNode = target.closest('[data-report-key]');
    const reportKey =
      reportNode instanceof HTMLElement
        ? reportNode.dataset.reportKey || ''
        : '';
    const epath = getEPath(target);

    burialPoint('click', {
      dom_key: reportKey || undefined,
      epath,
    });
  };

  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
}

export function bindBurialPointToWindow() {
  if (typeof window === 'undefined') return;
  ensureGuestUid();
  window.burialPoint = burialPoint;
}

declare global {
  interface Window {
    burialPoint?: typeof burialPoint;
  }
}
